import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Link, Typography, TypographyVariant, Unstable_Grid2 as Grid } from '@mui/material';
import { uniqBy } from 'lodash';
import FlagIcon from '@mui/icons-material/Flag';

import { GridLayoutSizes } from 'types/app';
import { CustomSVG, UserAvatarGroup } from 'components';
import { useContent } from 'hooks';
import { generateLink } from 'Links';
import { getFullName } from 'helpers';

import { getProviderDisplay, getTitle, hasCreator, isEngageable, isTextOnly } from '../helpers';
import { useCanShare } from '../hooks';
import { Date, Engage, PostMenu, Reactions, Share, ShareCountModal } from '../widgets';
import moment from 'moment/moment';
import theme from 'theme';

interface Props {
  variant: GridLayoutSizes;
  enableMenu?: boolean;
  newLineActions?: boolean;
}

const styles = {
  spacer: {
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
  icon: {
    verticalAlign: 'middle',
    fontSize: theme.typography.pxToRem(18),
  },
  orange: {
    color: theme.palette.warning.dark,
  },
  wrapper: {
    position: 'relative',
  },
  container: {
    position: 'absolute',
    top: 8,
    right: 8,
  },
} as const;

const ContentSection: FC<Props> = ({ variant, enableMenu, newLineActions }) => {
  const content = useContent();
  const { t } = useTranslation();

  const showShare = useCanShare(content);
  const showEngage = isEngageable(content);
  const title = getTitle(content);

  const {
    share_count,
    is_important,
    important_to_share,
    shared_by,
    content_id,
    publish_at,
    reacted_by,
  } = content;
  const providerDisplay = getProviderDisplay(content);
  const hasPostCreator = hasCreator(content);
  const postCreator = getFullName(content.user);
  const LinkContentComponent = generateLink(`/content/${content.content_id}`);
  const LinkUserComponent = hasPostCreator && generateLink(`/profile/${content?.user?.id}`);
  const showAvatars = !!shared_by.length || !!reacted_by.length;
  const avatarUsers = uniqBy(shared_by.concat(reacted_by), user => user.id);

  let titleVariant: TypographyVariant;
  let titleStyle;

  switch (variant) {
    case GridLayoutSizes.Small:
    case GridLayoutSizes.Medium:
      titleVariant = 'h6';
      titleStyle = theme.mixins.clampInline(2);
      break;
    case GridLayoutSizes.Large:
      titleVariant = 'h4';
      titleStyle = theme.mixins.clampInline(4);
      break;
  }

  if (isTextOnly(content)) {
    titleStyle = theme.mixins.clampInline(8);
    titleVariant = 'body1';
  }

  function renderSpecialStates() {
    if (is_important) {
      return (
        <Grid container alignItems="center" spacing={1}>
          <Grid>
            <FlagIcon color="primary" sx={{ ...styles.orange, ...styles.icon }} />
          </Grid>
          <Grid>
            <Typography variant="body2" sx={styles.orange}>
              {t('common:important')}
            </Typography>
          </Grid>
        </Grid>
      );
    }

    if (important_to_share && showShare) {
      return (
        <Grid container alignItems="center" spacing={1}>
          <Grid>
            <CustomSVG color="primary" size={18} sx={styles.icon} name="share" />
          </Grid>
          <Grid>
            <Typography variant="body2" color="primary">
              {t('content:recommendedToShare')}
            </Typography>
          </Grid>
        </Grid>
      );
    }

    return null;
  }

  function renderMeta() {
    if (!providerDisplay && !hasPostCreator) {
      return <Date contentId={content_id} publishAt={publish_at} />;
    }

    return (
      <Grid container alignItems="center" wrap="nowrap">
        {providerDisplay && (
          <Grid>
            <Typography display="block" noWrap color="textSecondary">
              {providerDisplay}
            </Typography>
          </Grid>
        )}
        {hasPostCreator && (
          <Fragment>
            {providerDisplay && (
              <Grid>
                <Box component="span" sx={styles.spacer}>
                  /
                </Box>
              </Grid>
            )}
            <Grid>
              <Link
                noWrap
                display="block"
                underline="none"
                component={LinkUserComponent}
                color="textSecondary"
              >
                {postCreator}
              </Link>
            </Grid>
          </Fragment>
        )}
        <Fragment>
          <Grid>
            <Box component="span" sx={styles.spacer}>
              /
            </Box>
          </Grid>
          <Grid>
            <Link
              noWrap
              display="block"
              underline="none"
              color="textSecondary"
              component={generateLink(`/content/${content_id}`)}
            >
              {moment.unix(publish_at).fromNow()}
            </Link>
          </Grid>
        </Fragment>
      </Grid>
    );
  }

  function renderCounts() {
    return (
      <Grid container spacing={2} alignItems="center">
        <Grid>
          <Reactions />
        </Grid>
        {!!share_count && (
          <Grid>
            <ShareCountModal />
          </Grid>
        )}
      </Grid>
    );
  }

  function renderActions() {
    if (!showEngage && !showShare) {
      return null;
    }

    return (
      <Grid container spacing={1} alignItems="center">
        {showShare && (
          <Grid>
            <Share />
          </Grid>
        )}
        {showEngage && (
          <Grid>
            <Engage />
          </Grid>
        )}
      </Grid>
    );
  }

  return (
    <Box paddingX={2} paddingBottom={2} paddingTop={1.5} sx={styles.wrapper}>
      {enableMenu && (
        <Box sx={styles.container}>
          <PostMenu size="small" />
        </Box>
      )}
      {renderSpecialStates()}
      {title && (
        <Link
          style={titleStyle}
          display="block"
          component={LinkContentComponent}
          underline="none"
          variant={titleVariant}
        >
          {title}
        </Link>
      )}
      {renderMeta()}
      {showAvatars && (
        <Box marginTop={0.5} marginBottom={1}>
          <UserAvatarGroup max={12} users={avatarUsers} size={32} />
        </Box>
      )}
      <Box marginTop={1}>
        {!newLineActions && (
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid>{renderCounts()}</Grid>
            <Grid>{renderActions()}</Grid>
          </Grid>
        )}
        {newLineActions && (
          <Fragment>
            {renderCounts()}
            <Box marginTop={0.5}>{renderActions()}</Box>
          </Fragment>
        )}
      </Box>
    </Box>
  );
};

ContentSection.defaultProps = {
  enableMenu: false,
  newLineActions: false,
};

export default ContentSection;
